import { useEffect, useRef, useCallback } from 'react';
import type { RefObject } from 'react';
import type { InfrastructureGraphHandle } from '@/components/InfrastructureGraph';
import type { CytoElement } from '@/types/dns';
import { useAuthStore } from '@/store/authStore';

const WS_BASE_DELAY_MS = 1000;
const WS_MAX_DELAY_MS = 30_000;
const WS_BUFFER_MS = 100;
const WS_JITTER_MS = 500;

interface UseWebSocketOptions {
  jobId: string | null;
  graphRef: RefObject<InfrastructureGraphHandle | null>;
  onError?: (msg: string) => void;
}

export function useWebSocket({ jobId, graphRef, onError }: UseWebSocketOptions): void {
  const wsRef = useRef<WebSocket | null>(null);
  const bufferRef = useRef<CytoElement[]>([]);
  const flushTimerRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const attemptRef = useRef(0);
  const activeJobRef = useRef<string | null>(null);

  // Callers pass an inline arrow for onError, so its identity changes on every
  // render. Holding it in a ref keeps `connect` — and therefore the connection
  // effect — stable; depending on it directly tore down and reopened the socket
  // on every render, including each progress-poll tick mid-scan.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const flushBuffer = useCallback(() => {
    const buffer = bufferRef.current;
    if (buffer.length === 0) return;
    bufferRef.current = [];
    flushTimerRef.current = null;
    graphRef.current?.batchAdd(buffer);
  }, [graphRef]);

  const connect = useCallback(
    (id: string) => {
      // Tear down any existing connection
      if (wsRef.current) {
        wsRef.current.onclose = null; // Prevent reconnect loop on intentional close
        wsRef.current.close();
        wsRef.current = null;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const authToken = useAuthStore.getState().token;
      const tokenParam = authToken ? `&token=${encodeURIComponent(authToken)}` : '';
      const url = `${protocol}://${window.location.host}/ws?job_id=${encodeURIComponent(id)}${tokenParam}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0; // Reset backoff on successful connect
      };

      ws.onmessage = (event: MessageEvent) => {
        let parsed: CytoElement | null = null;
        try {
          parsed = JSON.parse(event.data as string) as CytoElement;
        } catch {
          return; // Ignore malformed messages
        }
        bufferRef.current.push(parsed);
        if (flushTimerRef.current === null) {
          flushTimerRef.current = window.setTimeout(flushBuffer, WS_BUFFER_MS);
        }
      };

      ws.onerror = () => {
        onErrorRef.current?.('WebSocket connection error');
        // onerror always precedes onclose; clearing here prevents a dangling reference
        // if the caller tears down the hook synchronously in response to the error.
        wsRef.current = null;
      };

      ws.onclose = (evt) => {
        wsRef.current = null;
        // Don't reconnect on intentional close (code 1000) or if job changed
        if (evt.code === 1000 || activeJobRef.current !== id) return;
        // Exponential backoff with jitter
        const attempt = attemptRef.current;
        const base = Math.min(WS_BASE_DELAY_MS * Math.pow(2, attempt), WS_MAX_DELAY_MS);
        const jitter = (Math.random() * 2 - 1) * WS_JITTER_MS;
        const delay = Math.max(0, base + jitter);
        attemptRef.current = attempt + 1;
        reconnectTimerRef.current = window.setTimeout(() => {
          if (activeJobRef.current === id) {
            connect(id);
          }
        }, delay);
      };
    },
    [flushBuffer],
  );

  useEffect(() => {
    // Clear reconnect and flush timers on jobId change or unmount
    const clearTimers = () => {
      if (flushTimerRef.current !== null) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      // Flush any buffered elements before teardown
      flushBuffer();
    };

    if (!jobId) {
      activeJobRef.current = null;
      clearTimers();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000);
        wsRef.current = null;
      }
      return;
    }

    activeJobRef.current = jobId;
    attemptRef.current = 0;
    clearTimers();
    connect(jobId);

    return () => {
      activeJobRef.current = null;
      clearTimers();
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [jobId, connect, flushBuffer]);
}
